import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, UserPlus, Menu, X } from 'lucide-react';
import { useProfile } from '../hooks/useProfile';
import { UserMenu } from './UserMenu';
import { NotificationBell } from './NotificationBell';
import { motion, AnimatePresence } from 'framer-motion';
//import logo from '../assets/jamnation-logo.svg';

function Navbar() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Prevent body scroll when menu is open
    document.body.style.overflow = !isMenuOpen ? 'hidden' : 'unset';
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'unset';
  };

  return (
    <nav className="sticky top-0 z-50 bg-dark/80 backdrop-blur-lg md:border-b md:border-gray-custom/10">
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center space-x-12">
            <Link to="/" className="flex items-center group" onClick={closeMenu}>
              <div id="swiss-flag" className="flex w-7 h-7 flex-col justify-center items-center mr-3">
                <div className="swiss-flag-cross"></div>
              </div>
              <span className="font-tilt-warp text-2xl md:text-3xl light">jamnation</span>
              {/*<img width={210} src={logo} />*/}
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link 
                aria-label="Bands"
                to="/bands" 
                className={`nav-link ${location.pathname.startsWith('/bands') ? 'text-light' : ''}`}
              >
                <span>{t('nav.bands')}</span>
              </Link>
              <Link 
                aria-label="Musicians"
                to="/musicians" 
                className={`nav-link ${location.pathname.startsWith('/musicians') ? 'text-light' : ''}`}
              >
                <span>{t('nav.musicians')}</span>
              </Link>
              <Link 
                aria-label="Agenda"
                to="/agenda" 
                className={`nav-link ${location.pathname.startsWith('/agenda') ? 'text-light' : ''}`}
              >
                <span>{t('nav.agenda')}</span>
              </Link>
              <Link 
                aria-label="Stories"
                to="/stories" 
                className={`nav-link ${location.pathname.startsWith('/stories') ? 'text-light' : ''}`}
              >
                <span>{t('nav.stories')}</span>
              </Link>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {user ? (
              <div className="flex items-center space-x-4">
                {/* Desktop notification bell */}
                <div className="hidden md:block relative">
                  <NotificationBell />
                </div>
                <UserMenu user={user} profile={profile} onSignOut={signOut} />
              </div>
            ) : (
              <div className="hidden md:flex items-center space-x-6">
                <Link 
                  to="/login" 
                  className={`nav-link ${location.pathname === '/login' ? 'text-light' : ''}`}
                >
                  <span className="hidden md:block">{t('nav.signIn')}</span>
                </Link>
                <Link 
                  to="/register" 
                  className={`btn-outline ${location.pathname === '/register' ? 'border-light/40' : ''}`}
                >
                  <span className="flex items-center space-x-2">
                    <span className="hidden md:block">{t('nav.signUp')}</span>
                  </span>
                </Link>
              </div>
            )}

            {/* Mobile menu button - moved to the right */}
            <button
              aria-label="Menu"
              onClick={toggleMenu}
              className="md:hidden p-2 text-light/60 hover:text-light transition-colors ml-4"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 top-20 bg-dark/95 backdrop-blur-lg md:hidden z-40"
          >
            <div className="flex flex-col p-6 space-y-6 bg-dark/90">
              {/* Mobile notification bell for authenticated users */}
              {user && (
                <div className="flex items-center justify-between border-b border-gray-custom/10 pb-4">
                  <span className="text-light/60 text-sm">Notifications</span>
                  <NotificationBell />
                </div>
              )}

              <Link 
                to="/bands" 
                className={`text-xl ${location.pathname.startsWith('/bands') ? 'text-light' : 'text-light/60'}`}
                onClick={closeMenu}
              >
                {t('nav.bands')}
              </Link>
              <Link 
                to="/musicians" 
                className={`text-xl ${location.pathname.startsWith('/musicians') ? 'text-light' : 'text-light/60'}`}
                onClick={closeMenu}
              >
                {t('nav.musicians')}
              </Link>
              <Link 
                to="/agenda" 
                className={`text-xl ${location.pathname.startsWith('/agenda') ? 'text-light' : 'text-light/60'}`}
                onClick={closeMenu}
              >
                {t('nav.agenda')}
              </Link>
              <Link 
                to="/stories" 
                className={`text-xl ${location.pathname.startsWith('/stories') ? 'text-light' : 'text-light/60'}`}
                onClick={closeMenu}
              >
                {t('nav.stories')}
              </Link>

              {!user && (
                <div className="pt-6 border-t border-gray-custom/10 space-y-4">
                  <Link 
                    to="/login" 
                    className="flex items-center text-light underline"
                    onClick={closeMenu}
                  >
                    <span>{t('nav.signIn')}</span>
                  </Link>
                  <Link 
                    to="/register" 
                    className="flex items-center text-light underline"
                    onClick={closeMenu}
                  >
                    <span>{t('nav.signUp')}</span>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default Navbar;